import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { ZabbixPackagesResponse } from '@/types/api';

/**
 * Live-Paketstand eines Systems aus Zabbix (On-Demand, keine Persistierung).
 *
 * staleTime 300s = Backend-Cache-TTL; solange dient der Query-Cache als
 * zweite Schicht und der Tab-Wechsel löst keinen neuen Request aus.
 * `enabled` von außen steuern (nur aktiver Tab), damit der Abruf nicht
 * beim Laden der System-Detailseite mitfeuert.
 */
export function useZabbixPackages(
  systemId: string | undefined,
  opts: { enabled?: boolean } = {}
): ReturnType<typeof useQuery<ZabbixPackagesResponse>> {
  return useQuery<ZabbixPackagesResponse>({
    queryKey: queryKeys.systems.zabbixPackages(systemId ?? ''),
    queryFn: () =>
      api.get<ZabbixPackagesResponse>(`/systems/${systemId ?? ''}/zabbix-packages`),
    enabled: Boolean(systemId) && (opts.enabled ?? true),
    staleTime: 300_000,
    // 503 = Zabbix nicht konfiguriert/erreichbar - Retry bringt nichts.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 503) return false;
      return failureCount < 1;
    },
  });
}
